import { environment } from "../../../config/environment";
import { useState, useEffect } from 'react';
import EmailListDisplay from './EmailListDisplay';

export default function InvoiceModal({
  org,
  orgData,
  selectedMonth,
  setShowInvoiceModal
}) {
  const [invoiceData, setInvoiceData] = useState({
    userRate: 200.00,
    customItems: []
  });

  const [customItem, setCustomItem] = useState({
    description: '',
    amount: '',
    quantity: 1
  });

  const [loading, setLoading] = useState(false);
  const [invoiceResult, setInvoiceResult] = useState(null);
  const [error, setError] = useState('');
  const [showNames, setShowNames] = useState(false);
  const [showEmails, setShowEmails] = useState(false);
  const [draftInvoices, setDraftInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // Fetch draft invoices on component mount
  useEffect(() => {
    fetchDraftInvoices();
  }, []);

  const fetchDraftInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const response = await fetch(`${environment.apiUrl}/orgs/draft-invoices`, {
        headers: {
          'Content-Type': 'application/json',
          'token': localStorage.getItem('token')
        }
      });
      if (response.ok) {
        const drafts = await response.json();
        setDraftInvoices(drafts);
      }
    } catch (error) {
      console.error('Failed to fetch draft invoices:', error);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const calculateInvoice = () => {
    const usersTotal = orgData.activeDoctorsThisMonth * invoiceData.userRate;
    const customTotal = invoiceData.customItems.reduce((sum, item) =>
      sum + (item.amount * item.quantity), 0
    );

    const subtotal = usersTotal + customTotal;
    const total = subtotal;

    return {
      usersTotal,
      customTotal,
      subtotal,
      total
    };
  };

  const totals = calculateInvoice();

  const formatMonthLabel = (monthString) => {
    const [year, month] = monthString.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  const addCustomItem = () => {
    if (!customItem.description || !customItem.amount) return;

    setInvoiceData(prev => ({
      ...prev,
      customItems: [...prev.customItems, {
        ...customItem,
        amount: parseFloat(customItem.amount),
        id: Date.now()
      }]
    }));

    setCustomItem({ description: '', amount: '', quantity: 1 });
  };

  const removeCustomItem = (id) => {
    setInvoiceData(prev => ({
      ...prev,
      customItems: prev.customItems.filter(item => item.id !== id)
    }));
  };

  const generateInvoice = async () => {
    setLoading(true);
    setError('');

    try {
      const lineItems = [
        {
          description: `${orgData.activeDoctorsThisMonth} Active Users @ $${invoiceData.userRate.toFixed(2)} each`,
          amount: totals.usersTotal,
          quantity: 1,
          metadata: {
            type: 'users',
            count: orgData.activeDoctorsThisMonth,
            rate: invoiceData.userRate
          }
        },
        ...invoiceData.customItems.map(item => ({
          description: item.description,
          amount: item.amount,
          quantity: item.quantity,
          metadata: { type: 'custom' }
        }))
      ];

      const response = await fetch(`${environment.apiUrl}/orgs/create-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'token': localStorage.getItem('token')
        },
        body: JSON.stringify({
          orgName: org,
          monthYear: formatMonthLabel(selectedMonth),
          lineItems: lineItems.filter(item => item.amount > 0),
          metadata: {
            month: selectedMonth,
            activeDoctors: orgData.activeDoctorsThisMonth,
            generatedBy: 'admin_dashboard'
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setInvoiceResult(result);

      // Refresh draft invoices
      await fetchDraftInvoices();

    } catch (error) {
      console.error('Invoice generation failed:', error);
      setError(error.message || 'Failed to generate invoice');
    } finally {
      setLoading(false);
    }
  };
  
  const sendDraftInvoice = async (invoiceId) => {
    try {
      const response = await fetch(`${environment.apiUrl}/orgs/send-invoice/${invoiceId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'token': localStorage.getItem('token')
        }
      });

      if (!response.ok) {
        throw new Error('Failed to send invoice');
      }

      const result = await response.json();

      alert(`Invoice sent successfully! Status: ${result.status}`);
      await fetchDraftInvoices();

    } catch (error) {
      console.error('Failed to send draft invoice:', error);
      alert('Failed to send invoice: ' + error.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-[95%] max-w-6xl max-h-[95%] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-bold text-ktx-purple">
              Invoice Manager - {org}
            </h3>
            <p className="text-gray-600 text-sm mt-1">
              {formatMonthLabel(selectedMonth)} Billing Summary
            </p>
          </div>

          <button
            onClick={() => setShowInvoiceModal(false)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left Side - Invoice Management */}
          <div className="w-80 border-r border-gray-200 flex flex-col">
            {/* Header with refresh button */}
            <div className="p-4 border-b border-gray-200">
              <h4 className="font-semibold text-gray-800 mb-3">Invoice Management</h4>
              <button
                onClick={fetchDraftInvoices}
                disabled={loadingInvoices}
                className="w-full px-3 py-2 text-sm bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {loadingInvoices ? 'Refreshing...' : 'Refresh Drafts'}
              </button>
            </div>

            {/* Draft Invoices Section */}
            <div className="flex-1 flex flex-col">
              {/* Draft Invoices Section - Top Half */}
              <div className="flex-1 flex flex-col border-b border-gray-200">
                <div className="px-4 py-3 bg-blue-50 border-b border-blue-200">
                  <h5 className="font-medium text-blue-800 flex items-center gap-2">
                    💾 Draft Invoices ({draftInvoices.length})
                  </h5>
                </div>

                <div className="flex-1 overflow-y-auto p-3">
                  {draftInvoices.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">
                      <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-xs">No draft invoices</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {draftInvoices.map((invoice) => (
                        <div key={invoice.id} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="space-y-3">
                            <div className="flex justify-between items-start">
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-bold text-blue-800 truncate mb-1">
                                  {invoice.number || 'No Number'}
                                </p>
                                <p className="text-xs font-medium text-gray-900 truncate">
                                  {invoice.description}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Created: {new Date(invoice.created).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-bold text-blue-600">
                                  ${invoice.amount.toFixed(2)}
                                </p>
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                  Draft
                                </span>
                              </div>
                            </div>

                            <a
                              href={invoice.stripeUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full block px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center font-medium"
                            >
                              📋 Open in Stripe Dashboard
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Create New Invoice */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {!invoiceResult ? (
              <>
                {/* Usage Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-4">Usage Summary</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Active Users:</span>
                      <span className="font-medium ml-2">{orgData.activeDoctorsThisMonth}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Notes Generated:</span>
                      <span className="font-medium ml-2">{orgData.notesThisMonth}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Total Users:</span>
                      <span className="font-medium ml-2">{orgData.totalDoctors}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Activation Rate:</span>
                      <span className="font-medium ml-2">
                        {orgData.totalDoctors > 0
                          ? ((orgData.activeDoctorsThisMonth / orgData.totalDoctors) * 100).toFixed(1) + '%'
                          : '0%'
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {/* Active Users Details */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold">Active Users This Month</h4>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowNames(!showNames)}
                        className={`px-3 py-1 text-xs rounded-lg transition-colors font-medium ${showNames
                          ? 'bg-ktx-purple text-white shadow-sm'
                          : 'bg-gray-100 text-gray-700 hover:bg-ktx-purple hover:text-white border border-gray-300'
                          }`}
                      >
                        {showNames ? '👁️ Hide Names' : '👥 Show Names'}
                      </button>
                      <button
                        onClick={() => setShowEmails(!showEmails)}
                        className={`px-3 py-1 text-xs rounded-lg transition-colors font-medium ${showEmails
                          ? 'bg-ktx-purple text-white shadow-sm'
                          : 'bg-gray-100 text-gray-700 hover:bg-ktx-purple hover:text-white border border-gray-300'
                          }`}
                      >
                        {showEmails ? '👁️ Hide Emails' : '📧 Show Emails'}
                      </button>
                    </div>
                  </div>

                  <div className="text-center py-4">
                    <div className="text-3xl font-bold text-ktx-purple mb-1">
                      {orgData.activeDoctorsThisMonth}
                    </div>
                    <div className="text-sm text-gray-600">
                      Users will be billed at ${invoiceData.userRate.toFixed(2)} each
                    </div>
                  </div>

                  {(showNames || showEmails) && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      {showNames && (
                        <EmailListDisplay
                          nameList={orgData.activeUserNames}
                          setShowPreviousNames={setShowNames}
                          componentName="Names-Prev"
                          monthInQuestion={selectedMonth}
                        />
                      )}
                      {showEmails && (
                        <EmailListDisplay
                          emailList={orgData.activeUserEmails}
                          setShowPreviousEmails={setShowEmails}
                          componentName="Emails-Prev"
                          monthInQuestion={selectedMonth}
                        />
                      )}
                    </div>
                  )}
                </div>

                {/* Pricing Configuration */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-4">Pricing Configuration</h4>
                  <div className="max-w-xs">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rate per Active User ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={invoiceData.userRate}
                      onChange={(e) => setInvoiceData(prev => ({
                        ...prev,
                        userRate: parseFloat(e.target.value) || 0
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ktx-purple"
                    />
                  </div>
                </div>

                {/* Custom Line Items */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-4">Additional Charges</h4>

                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <input
                      type="text"
                      placeholder="Description"
                      value={customItem.description}
                      onChange={(e) => setCustomItem(prev => ({
                        ...prev,
                        description: e.target.value
                      }))}
                      className="col-span-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ktx-purple text-sm"
                    />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Amount"
                      value={customItem.amount}
                      onChange={(e) => setCustomItem(prev => ({
                        ...prev,
                        amount: e.target.value
                      }))}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ktx-purple text-sm"
                    />
                    <button
                      onClick={addCustomItem}
                      className="px-3 py-2 bg-ktx-purple text-white rounded-lg hover:bg-ktx-navy transition-colors text-sm"
                    >
                      Add
                    </button>
                  </div>

                  {invoiceData.customItems.length > 0 && (
                    <div className="space-y-2">
                      {invoiceData.customItems.map((item) => (
                        <div key={item.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                          <span className="text-sm">{item.description}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium">${(item.amount * item.quantity).toFixed(2)}</span>
                            <button
                              onClick={() => removeCustomItem(item.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Invoice Preview */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-4">Invoice Preview</h4>

                  <div className="space-y-3">
                    {totals.usersTotal > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm">{orgData.activeDoctorsThisMonth} Active Users @ ${invoiceData.userRate.toFixed(2)}</span>
                        <span className="font-medium">${totals.usersTotal.toFixed(2)}</span>
                      </div>
                    )}

                    {invoiceData.customItems.map((item) => (
                      <div key={item.id} className="flex justify-between items-center">
                        <span className="text-sm">{item.description}</span>
                        <span className="font-medium">${(item.amount * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}

                    <div className="border-t pt-3 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Subtotal</span>
                        <span className="font-medium">${totals.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center font-bold text-lg border-t pt-2">
                        <span>Total</span>
                        <span className="text-ktx-purple">${totals.total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Error Display */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-red-800">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Error: {error}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  <button
                    onClick={() => setShowInvoiceModal(false)}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={generateInvoice}
                    disabled={loading || totals.total <= 0}
                    className="flex-1 px-4 py-3 bg-ktx-purple text-white rounded-lg hover:bg-ktx-navy disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    {loading ? 'Creating...' : `Create Draft Invoice ($${totals.total.toFixed(2)})`}
                  </button>
                </div>
              </>
            ) : (
              /* Enhanced Success State with Stripe Links */
              <div className="text-center space-y-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <div className="flex items-center justify-center gap-3 text-green-800 mb-4">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <h4 className="text-xl font-semibold">
                      Draft Invoice Created Successfully!
                    </h4>
                  </div>

                  <div className="space-y-3 text-sm">
                    <p><strong>Invoice Number:</strong> {invoiceResult.invoiceNumber}</p>
                    <p><strong>Invoice ID:</strong> {invoiceResult.invoiceId}</p>
                    <p><strong>Organization:</strong> {org}</p>
                    <p><strong>Period:</strong> {formatMonthLabel(selectedMonth)}</p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                      <p className="text-blue-800 font-medium mb-2">Next Steps:</p>
                      <p className="text-blue-700 text-sm">
                        📋 Invoice created as draft in Stripe<br />
                        🔍 Review details and finalize<br />
                        📧 Send to customer when ready
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 flex gap-3">
                    <a
                      href={invoiceResult.stripeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      Open in Stripe Dashboard
                    </a>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setInvoiceResult(null);
                      setError('');
                      fetchDraftInvoices();
                    }}
                    className="flex-1 px-6 py-3 border border-ktx-purple text-ktx-purple rounded-lg hover:bg-ktx-purple hover:text-white transition-colors"
                  >
                    Create Another
                  </button>
                  <button
                    onClick={() => setShowInvoiceModal(false)}
                    className="flex-1 px-6 py-3 bg-ktx-purple text-white rounded-lg hover:bg-ktx-navy transition-colors font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}